import { useState, useEffect } from 'react';
import { fetchAuthSession } from '../utils/session';
import { Capability, hasCapability as computeHasCapability } from '../utils/capabilities';

export interface CapabilitiesState {
  /** Cognito `cognito:groups` on the caller's own JWT (may be empty). */
  groups: string[];
  /** `custom:business_id` on the caller's own JWT, trusted source only. */
  callerBusinessId: string | null;
  /** `sub` on the caller's own JWT, to tell "acting on my own account" apart (null until loaded). */
  callerSub: string | null;
  isLoading: boolean;
  /**
   * Mirrors capability_matrix.check_capability (SAM-538, backend PR #342):
   * true only if a group on the caller's OWN token grants `capability`,
   * tenant-wide or for the given `targetBusinessId`. Defaults
   * `targetBusinessId` to the caller's own business id when omitted, which
   * covers the common "does the current business I'm looking at grant me
   * this" case.
   *
   * Fails closed while the token hasn't loaded yet (isLoading) and on any
   * decode error — never opens up access that the backend wouldn't grant.
   */
  hasCapability: (capability: Capability, targetBusinessId?: string | null) => boolean;
}

/**
 * SAM-544 (N03): reads the SAME id token this app already decodes elsewhere
 * (see useUserPermissions.ts, utils/auth.ts) and evaluates it against the
 * SAM-538 capability matrix mirrored in utils/capabilities.ts.
 *
 * This does NOT call a backend endpoint — there isn't one that returns "my
 * capabilities" today. It is a client-side prediction of what the backend
 * will do, used to avoid showing controls (void/refund, service writes)
 * that would 403. The backend remains the enforcement point; this hook only
 * decides what to render. A stale or wrong prediction here degrades to
 * "hide the control", never to "allow something the backend would reject" —
 * see hasCapability's fail-closed default in utils/capabilities.ts.
 */
export const useCapabilities = (): CapabilitiesState => {
  const [groups, setGroups] = useState<string[]>([]);
  const [callerBusinessId, setCallerBusinessId] = useState<string | null>(null);
  const [callerSub, setCallerSub] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) {
          if (!cancelled) {
            setGroups([]);
            setCallerBusinessId(null);
          }
          return;
        }

        const parts = idToken.split('.');
        if (parts.length !== 3) {
          if (!cancelled) {
            setGroups([]);
            setCallerBusinessId(null);
          }
          return;
        }

        const payload = JSON.parse(atob(parts[1]));
        const rawGroups = payload['cognito:groups'] || [];
        const parsedGroups = Array.isArray(rawGroups) ? rawGroups : [rawGroups].filter(Boolean);

        if (!cancelled) {
          setGroups(parsedGroups.map((g: unknown) => String(g)));
          setCallerBusinessId(payload['custom:business_id'] || null);
          setCallerSub(payload['sub'] || null);
        }
      } catch (error) {
        console.error('useCapabilities: could not decode id token, defaulting to no capabilities', error);
        if (!cancelled) {
          setGroups([]);
          setCallerBusinessId(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasCapability = (capability: Capability, targetBusinessId?: string | null): boolean => {
    if (isLoading) return false;
    const target = targetBusinessId !== undefined ? targetBusinessId : callerBusinessId;
    return computeHasCapability(groups, capability, callerBusinessId, target);
  };

  return { groups, callerBusinessId, callerSub, isLoading, hasCapability };
};
